import React, { useId, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Btn, Checkbox, InsightBox, Overline } from "../../../ForBrands/ui/BrandUI";
import { ContextStrip, KeyValueRow, MobileCard, MobileCardHead, MobilePageHead, TextLink } from "../../../ForBrands/ui/mobile/MobileUI";
import { fmtMoney } from "../athleteUtils";
import { fmtDateTime } from "../../../ForBrands/brandDealUtils";
import { DealScreen, GuardianCard } from "./OfferMobile";

// Athlete Mode · contract review on phones (Figma 2000:1011 "07 · Contract
// review"). The page keeps the data, the checklist state and the sign /
// request / decline handlers; this is only the phone tree. The sticky bar
// reads Sign, Request changes, then Decline Offer (Figma order). The scope
// of work is shown inline, clamped to 240px with a fade and a "Read full
// scope" toggle (decision D-a19), since contracts can be long.

const SCOPE_MAX = 240;

const SIGN_BADGE = {
  signed: { label: "Signed ✓", dot: "#1f9d55" },
  awaiting: { label: "Awaiting Signature", dot: "#edd200" },
  cosign: { label: "Awaiting Co-sign", dot: "#edd200" },
};

const SignatureRow = ({ name, state }) => (
  <div className="flex items-center gap-3">
    <span className="min-w-0 flex-1 break-words bm-body-m text-bm-ink">{name}</span>
    <Badge tone="outline" dot={SIGN_BADGE[state].dot}>
      {SIGN_BADGE[state].label}
    </Badge>
  </div>
);

// Multi-line values (deliverables, payment schedule) line up at the top.
const Lines = ({ items }) => (
  <span className="flex flex-col items-end">
    {items.map(([key, text]) => (
      <span key={key}>{text}</span>
    ))}
  </span>
);

const ScopeText = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const ref = useRef(null);
  const id = useId();

  // Measured, so a short scope shows no toggle; re-measured when the text
  // changes, the window resizes (a rotated phone rewraps the text) and the
  // web fonts finish loading (Inter wraps differently from the fallback).
  // jsdom has no layout (scrollHeight 0), so it shows no toggle there.
  useLayoutEffect(() => {
    let live = true;
    const measure = () => {
      const el = ref.current;
      if (live && el) setOverflows(el.scrollHeight > SCOPE_MAX + 1);
    };
    measure();
    window.addEventListener("resize", measure);
    document.fonts?.ready?.then(measure).catch(() => {});
    return () => {
      live = false;
      window.removeEventListener("resize", measure);
    };
  }, [text]);

  const clamped = overflows && !expanded;
  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <p
          id={id}
          ref={ref}
          className={`whitespace-pre-wrap break-words bm-body-s text-bm-muted ${clamped ? "max-h-[240px] overflow-hidden" : ""}`.trim()}
        >
          {text}
        </p>
        {clamped && <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white to-transparent" />}
      </div>
      {overflows && (
        <TextLink arrow={false} aria-expanded={expanded} aria-controls={id} onClick={() => setExpanded((v) => !v)} className="self-start">
          {expanded ? "Show less" : "Read full scope"}
        </TextLink>
      )}
    </div>
  );
};

const ContractMobile = ({
  onBack,
  campaign,
  brand,
  athlete,
  offer,
  contract,
  range,
  subtitle,
  guardianBadge,
  guardianText,
  showCircleLink,
  exclusivityText,
  brandSigned,
  athleteSigned,
  guardianState,
  showGuardianRow,
  guardianName,
  fairness,
  checklist,
  checks,
  onCheck,
  statusLine,
  signed,
  busy,
  signDisabled,
  signLabel,
  onSign,
  onOpenTracker,
  onRequest,
  onDecline,
  onDownload,
  modal,
}) => {
  const deliverables = offer.deliverableLines || [];
  const milestones = offer.milestones || [];
  const terms = [
    { label: "Compensation", value: `${fmtMoney(offer.compensation, { cents: true })} · paid in full to you` },
    { label: "Deliverables", value: <Lines items={deliverables.map((l) => [l, l])} />, multi: true },
    { label: "Payment schedule", value: <Lines items={milestones.map((m) => [m.id, `${fmtMoney(m.amount)} ${m.label}`])} />, multi: true },
    { label: "Timeline", value: range },
    { label: "Exclusivity", value: exclusivityText },
    offer.adUsageDays ? { label: "Ad usage rights", value: `${offer.adUsageDays} days` } : null,
  ].filter(Boolean);

  return (
    <DealScreen
      title="Contract review"
      onBack={onBack}
      backLabel="Back to My Deals"
      strip={<ContextStrip title={campaign?.name} badge={<Badge tone="outline">Contract review</Badge>} meta={`${brand.company} · ${range}`} />}
      actions={
        <>
          {signed ? (
            <Btn size="lg" className="w-full" onClick={onOpenTracker}>
              Open campaign tracker
            </Btn>
          ) : (
            <Btn size="lg" className="w-full" onClick={onSign} disabled={signDisabled}>
              {signLabel}
            </Btn>
          )}
          <Btn size="lg" variant="secondary" className="w-full" onClick={onRequest} disabled={busy || signed}>
            Request changes
          </Btn>
          {!signed && (
            <Btn variant="danger" className="w-full" onClick={onDecline} disabled={busy}>
              Decline Offer
            </Btn>
          )}
        </>
      }
      modal={modal}
    >
      <MobilePageHead eyebrow="Deal flow" eyebrowBrand title="Review & Sign Contract" sub={subtitle} />

      {athlete.isMinor && (
        <GuardianCard badge={guardianBadge && <Badge tone="dark">{guardianBadge}</Badge>}>
          <p>{guardianText}</p>
          {showCircleLink && (
            <Link to="/athlete/circle" className="mt-[10px] inline-block bm-label-l text-bm-orange">
              Open My Circle
            </Link>
          )}
        </GuardianCard>
      )}

      <MobileCard>
        <MobileCardHead title="Agreed Deal Terms" />
        <div>
          {terms.map((t, i) => (
            <KeyValueRow
              key={t.label}
              label={t.label}
              value={t.value}
              last={i === terms.length - 1}
              className={t.multi ? "!items-start" : ""}
            />
          ))}
        </div>
      </MobileCard>

      <MobileCard>
        <MobileCardHead title="Signature Progress" />
        <SignatureRow name={`${brand.company} (Brand)`} state={brandSigned ? "signed" : "awaiting"} />
        <SignatureRow name={`${athlete.fullName} (You)`} state={athleteSigned ? "signed" : "awaiting"} />
        {showGuardianRow && <SignatureRow name={`${guardianName} (Guardian)`} state={guardianState === "signed" ? "signed" : "cosign"} />}
        {contract && (
          <div className="flex flex-col items-start gap-1 pt-1">
            <TextLink onClick={onDownload}>{contract.hasFile ? "Download contract" : "Download terms"}</TextLink>
            {contract.sentAt && <span className="bm-body-xs text-bm-muted">Sent {fmtDateTime(contract.sentAt)}</span>}
          </div>
        )}
      </MobileCard>

      <MobileCard>
        <MobileCardHead title="Scope of work" />
        <ScopeText text={contract?.scopeOfWork || offer.scopeOfWork || "The brand has not attached a scope of work."} />
      </MobileCard>

      <InsightBox compact title="Cache AI fairness review">
        {fairness}
      </InsightBox>

      <MobileCard>
        <Overline>Contract checklist</Overline>
        {checklist.map((c) => (
          <Checkbox key={c.id} checked={!!checks[c.id]} onChange={(v) => onCheck(c.id, v)} label={c.label} />
        ))}
        {statusLine && <p className="bm-body-s text-bm-muted">{statusLine}</p>}
      </MobileCard>
    </DealScreen>
  );
};

export default ContractMobile;
